import {useRef} from 'react';
import {useScroll, useTransform, useSpring} from 'motion/react';
import {SocialProofData} from '../../mockData';
import {ANIMATION_CONFIG} from './animation-config';

// Constants for scroll calculation
const PANEL_WIDTH = 720;
const CONTAINER_MAX_WIDTH = 1340;
const CONTAINER_PADDING = 32;
const GAP_SIZE = 24;
const STUDIO_SCENE_PADDING = 0.2; // 20% of panel width

// Responsive breakpoints for different screen sizes
const BREAKPOINTS = {
    SMALL: 1440, // 14-15" laptops, 24" monitors (1920x1080)
    LARGE: 2560, // 27" monitors (2560x1440), 32" monitors (4K)
} as const;

/**
 * Get responsive scroll multiplier based on viewport width
 */
const getResponsiveMultiplier = (viewportWidth: number): number => {
    if (viewportWidth < BREAKPOINTS.SMALL) {
        // Small screens (14-15" laptops, 24" Full HD) - reduce scroll
        return 0.8;
    } else if (viewportWidth > BREAKPOINTS.LARGE) {
        // Large screens (27" 2K, 32" 4K) - increase scroll
        return 1.3;
    }
    // Medium screens (16" laptops, standard monitors) - normal scroll
    return 1.0;
};

/**
 * Calculate the total horizontal scroll amount needed to show all content
 * and align with footer edges when the effect stops
 * Optimized for different screen sizes
 */
const calculateScrollAmount = (itemCount: number) => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const effectiveContainerWidth =
        Math.min(viewportWidth, CONTAINER_MAX_WIDTH) - CONTAINER_PADDING;
    const totalContentWidth = itemCount * PANEL_WIDTH + (itemCount - 1) * GAP_SIZE;
    const scrollNeeded = totalContentWidth - effectiveContainerWidth;
    const padding = PANEL_WIDTH * STUDIO_SCENE_PADDING;

    // Apply responsive multiplier based on screen size
    const responsiveMultiplier = getResponsiveMultiplier(viewportWidth);
    const baseScrollAmount = -(scrollNeeded + padding);

    return baseScrollAmount * responsiveMultiplier;
};

export const useScrollAnimation = () => {
    const wrapperRef = useRef<HTMLElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Setup scroll tracking
    const {scrollYProgress} = useScroll({
        target: wrapperRef,
        offset: [...ANIMATION_CONFIG.SCROLL.offset],
    });

    // Calculate scroll amounts
    const scrollAmount = calculateScrollAmount(SocialProofData.length);
    const pinStart = ANIMATION_CONFIG.SCROLL.pin.startOffset;
    const pinEnd = pinStart + ANIMATION_CONFIG.SCROLL.pin.duration;

    // Create horizontal transform that stops when pin ends
    const xRaw = useTransform(scrollYProgress, [pinStart, pinEnd], [0, scrollAmount], {
        clamp: true,
    });
    const x = useSpring(xRaw, ANIMATION_CONFIG.SCROLL.spring);

    // Calculate component height
    const baseHeight = 20;
    const pinHeight = (SocialProofData.length - 1) * 35;
    const endHeight = 40;
    const adjustedPinHeight = pinHeight * ANIMATION_CONFIG.SCROLL.pin.duration;
    const totalScrollHeight = `${baseHeight + adjustedPinHeight + endHeight}vh`;

    return {
        wrapperRef,
        containerRef,
        x,
        scrollYProgress,
        totalScrollHeight,
    };
};
